import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../hooks/useAuth';
import { Monitor, Smartphone, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { SubNav } from '@/components/ui/sub-nav';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.frolic.so';

interface Session {
  id: string;
  device: string;
  browser: string;
  ip: string;
  createdAt: string;
  lastActive: string;
  current: boolean;
}

function SessionsPage() {
  const { accessToken } = useAuth();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['sessions'],
    queryFn: async () => {
      const response = await fetch(`${API_URL}/v1/auth/sessions`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) throw new Error('Failed to fetch sessions');
      return response.json();
    },
  });

  const revokeMutation = useMutation({
    mutationFn: async (sessionId: string) => {
      const response = await fetch(`${API_URL}/v1/auth/sessions/${sessionId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) throw new Error('Failed to revoke session');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sessions'] });
    },
  });

  const sessions: Session[] = data?.sessions || [];

  const subNavItems = [
    { to: '/security', label: 'Password' },
    { to: '/security/tokens', label: 'API Tokens' },
    { to: '/security/sessions', label: 'Sessions' },
    { to: '/security/two-factor', label: 'Two-Factor' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Active Sessions</h1>
        <p className="text-muted-foreground">Manage your active login sessions</p>
      </div>

      <SubNav items={subNavItems} />

      <div className="max-w-2xl">
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </div>
        )}

        {sessions.length === 0 && !isLoading && (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                <Monitor className="h-6 w-6 text-muted-foreground" />
              </div>
              <h3 className="mt-4 text-lg font-medium text-foreground">No active sessions</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                You don't have any active sessions
              </p>
            </CardContent>
          </Card>
        )}

        {sessions.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Your Sessions</CardTitle>
              <CardDescription>
                Devices where you're currently logged in
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {sessions.map((session) => (
                  <div
                    key={session.id}
                    className={`flex items-center justify-between rounded-lg border p-4 ${
                      session.current
                        ? 'border-green-500/30 bg-green-500/5'
                        : 'border-border'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`flex h-10 w-10 items-center justify-center rounded-full ${
                          session.current ? 'bg-green-500/20' : 'bg-muted'
                        }`}
                      >
                        {session.device?.toLowerCase().includes('mobile') ? (
                          <Smartphone
                            className={`h-5 w-5 ${
                              session.current ? 'text-green-400' : 'text-muted-foreground'
                            }`}
                          />
                        ) : (
                          <Monitor
                            className={`h-5 w-5 ${
                              session.current ? 'text-green-400' : 'text-muted-foreground'
                            }`}
                          />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-foreground">
                            {session.browser || 'Unknown Browser'}
                          </span>
                          {session.current && (
                            <Badge variant="success">Current Session</Badge>
                          )}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {session.ip || 'Unknown IP'} · Last active{' '}
                          {new Date(session.lastActive).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                    {!session.current && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-destructive hover:bg-destructive hover:text-destructive-foreground"
                        onClick={() => {
                          if (confirm('Revoke this session?')) {
                            revokeMutation.mutate(session.id);
                          }
                        }}
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Revoke
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

export const Route = createFileRoute('/security/sessions')({
  component: SessionsPage,
});
